
import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Loader } from "lucide-react";
import { TbArrowsLeftRight } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";

import { IoChatbubblesOutline } from "react-icons/io5";
import { FiEdit2 } from "react-icons/fi";
import { RiFileEditLine, RiCloseLargeFill } from "react-icons/ri";
import { ImStarEmpty } from "react-icons/im";
import { ReviewTileItem } from "@/types/tile.types";
import { useTileInsert } from "@/hooks/useTileInsert";
import { useTileComment } from "@/hooks/useTileComment";
import { useTileCopy } from "@/hooks/useTileCopy";
import { useTileDismiss } from "@/hooks/useTileDismiss";
import { useSubmitFeedback } from "@/hooks/useSubmitFeedback";
import { toast } from "sonner";
import { ISubmitFeedbackRequest } from "@/types/discuss.types";
import { useTileReplace } from "@/hooks/useTileReplace";
import { useUpdateTileStatus } from "@/hooks/useUpdateTileStatus";
import { useWriteIt } from "@/hooks/useWriteIt";
import { EditTileDialog } from "./EditTileDialog";

const RatingDialog = React.lazy(() => import("./RatingDialog"));

interface TileActionsProps {
  item: ReviewTileItem;
  documentId: string;
  isBulkOperationInProgress: boolean;
  onProcessingStateChange: (isProcessing: boolean) => void;
}

export const TileActions: React.FC<TileActionsProps> = ({
  item,
  documentId,
  isBulkOperationInProgress,
  onProcessingStateChange,
}) => {
  const [showRatingDialog, setShowRatingDialog] = React.useState(false);
  const [isSubmittingRating, setIsSubmittingRating] = React.useState(false);
  const { mutate: updateTileStatus } = useUpdateTileStatus();

  const insertHook = useTileInsert({
    onSuccess: (message) => {
      toast.success(message);
      if (item.query && item.originalData?.tile_id !== undefined) {
        updateTileStatus({
          document_id: [documentId],
          query_term: [item.query],
          tile_ids: [item.originalData.tile_id],
          isCompleted: true,
          isFailed: false,
        });
      }
    },
    onError: (error) => toast.error(error),
  });

  const commentHook = useTileComment({
    onSuccess: (message) => toast.success(message),
    onError: (error) => toast.error(error),
  });

  const copyHook = useTileCopy({
    onSuccess: (message) => toast.success(message),
    onError: (error) => toast.error(error),
  });

  const dismissHook = useTileDismiss({
    onSuccess: (message) => toast.success(message),
    onError: (error) => toast.error(error),
    documentId: documentId,
  });

  const replaceHook = useTileReplace({
    onSuccess: (message) => {
      toast.success(message);
      if (item.query && item.originalData?.tile_id !== undefined) {
        updateTileStatus({
          document_id: [documentId],
          query_term: [item.query],
          tile_ids: [item.originalData.tile_id],
          isCompleted: true,
          isFailed: false,
        });
      }
    },
    onError: (error) => toast.error(error),
  });

  const { mutateAsync: submitFeedback } = useSubmitFeedback();

  const { mutate: writeIt } = useWriteIt();

  const isItemBeingProcessed =
    insertHook.insertingTileId === item.id ||
    commentHook.addingCommentTileId === item.id ||
    copyHook.copyingTileId === item.id ||
    dismissHook.dismissingTileId === item.id ||
    replaceHook.replacingTileId === item.id;

  React.useEffect(() => {
    onProcessingStateChange(isItemBeingProcessed);
  }, [isItemBeingProcessed, onProcessingStateChange]);

  const isAnyOperationInProgress =
    isBulkOperationInProgress ||
    insertHook.isInserting ||
    commentHook.isAddingComments ||
    copyHook.isCopying ||
    dismissHook.isDismissing ||
    replaceHook.isReplacing;

  const handleRatingSubmit = async (rating: number, comment: string) => {
    if (!documentId || !item.documentName) {
      toast.error("Submission Failed", {
        description: "Document context is missing.",
      });
      return;
    }

    setIsSubmittingRating(true);
    try {
      const feedbackPayload = {
        document_id: [documentId],
        source: "Review" as ISubmitFeedbackRequest["source"],
        prompt: item.queryType || null,
        query: item.query || "",
        response: item.analysis || item.explanation || item.reason || "",
        rating: rating as 1 | 2 | 3 | 4 | 5,
        comment: comment,
        current_document_id: documentId,
        document_title: [item.documentName],
      };

      await submitFeedback(feedbackPayload);

      toast.success("Feedback Submitted", {
        description: "Thank you for your feedback",
      });
      setShowRatingDialog(false);
    } catch (error) {
      toast.error("Submission Failed", {
        description: "Failed to submit feedback. Please try again.",
      });
    } finally {
      setIsSubmittingRating(false);
    }
  };

  const handleAction = async (action: string) => {
    try {
      switch (action) {
        case "write":
          writeIt({
            document_id: [documentId],
            tile_id: item.originalData?.tile_id,
            query_term: item.query || "",
            document_type: "Incoming",
          });
          await insertHook.insertSingleTile(item);
          break;
        case "comment":
          await commentHook.addSingleComment(item);
          break;
        case "copy":
          await copyHook.copySingleTile(item);
          break;
        case "close":
          await dismissHook.dismissSingleTile(item);
          break;
        case "rate":
          setShowRatingDialog(true);
          break;
        case "insert":
          await replaceHook.replaceSingleTile(item);
          break;
        default:
          console.warn("Unknown tile action:", action);
      }
    } catch (error) {
      console.error(`Error in tile action ${action}: `, error);
    }
  };

  const tileActions = [
    {
      name: "close",
      icon: <RiCloseLargeFill className="size-3.5 text-neutral-400" />,
      tooltip: "Close tile",
      onClick: () => handleAction("close"),
      isLoading: dismissHook.dismissingTileId === item.id,
      disabled: isAnyOperationInProgress,
      className: "text-danger-200",
      condition: true,
    },
    {
      name: "edit",
      icon: (
        <EditTileDialog tile={item} queryTerm={item.query!}>
          <span>
            <RiFileEditLine className="size-3.5 text-neutral-400" />
          </span>
        </EditTileDialog>
      ),
      tooltip: "Edit tile",
      onClick: () => { },
      isLoading: false,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
    {
      name: "write",
      icon: <FiEdit2 className="size-3.5 text-neutral-400" />,
      tooltip: "Write it",
      onClick: () => handleAction("write"),
      isLoading: insertHook.insertingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: item.originalData?.status?.isWritable,
    },
    {
      name: "insert",
      icon: <TbArrowsLeftRight className="size-3.5 text-neutral-400" />,
      tooltip: "Replace Section",
      onClick: () => handleAction("insert"),
      isLoading: replaceHook.replacingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: item.originalData?.status?.isReplaceable,
    },
    {
      name: "copy",
      icon: <FaRegCopy className="size-3.5 text-neutral-400" />,
      tooltip: "Copy text",
      onClick: () => handleAction("copy"),
      isLoading: copyHook.copyingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
    {
      name: "rate",
      icon: <ImStarEmpty className="size-3.5 text-neutral-400" />,
      tooltip: "Rate this response",
      onClick: () => handleAction("rate"),
      isLoading: false,
      disabled: isAnyOperationInProgress,
      className: "text-yellow-500",
      condition: true,
    },
    {
      name: "comment",
      icon: <IoChatbubblesOutline className="size-3.5 text-neutral-400" />,
      tooltip: "Add Comment",
      onClick: () => handleAction("comment"),
      isLoading: commentHook.addingCommentTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
  ];

  const visibleActions = tileActions.filter((action) => action.condition);

  return (
    <div className="flex flex-col items-center gap-4 shrink-0">
      {visibleActions.map((action) => (
        <TooltipProvider key={action.name}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                role="button"
                className={`size-3.5 ${action.className || ""}`}
                onClick={action.onClick}
              >
                {action.isLoading ? <Loader className="animate-spin" /> : action.icon}
              </div>
            </TooltipTrigger>
            <TooltipContent>{action.tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}

      <React.Suspense fallback={null}>
        <RatingDialog
          isOpen={showRatingDialog}
          onOpenChange={setShowRatingDialog}
          onSubmit={handleRatingSubmit}
          isSubmitting={isSubmittingRating}
        />
      </React.Suspense>
    </div>
  );
};
